import { recognitionApi } from '../api/recognitionApi'
import type { CreateCompanyValuePayload } from '../api/recognitionApi'
import type {
  Recognition,
  RecognitionFilters,
  PaginatedResponse,
  CreateRecognitionPayload,
  AddReactionPayload,
  AddCommentPayload,
  LeaderboardEntry,
  LeaderboardFilters,
  BadgeItem,
  Reward,
  RedeemRewardPayload,
  RecognitionStats,
  RecognitionAnalytics,
  DashboardData,
  Employee,
  Comment,
  RecognitionType,
} from '../types/recognition'
import {
  recognitions as mockRecognitions,
  leaderboardData as mockLeaderboard,
  badges as mockBadges,
  rewards as mockRewards,
  currentUserStats as mockStats,
  employees as mockEmployees,
  companyValues as mockCompanyValues,
  analyticsChartData,
  analyticsMonths,
  deptAnalytics,
  valueDistribution,
} from '../pages/recognition/mockData'

// Toggle this when the backend is ready
const USE_MOCK = false

export class RecognitionService {
  async getRecognitions(
    filters?: RecognitionFilters,
  ): Promise<PaginatedResponse<Recognition>> {
    if (USE_MOCK) {
      let data = [...mockRecognitions]
      if (filters?.type && filters.type !== 'all') {
        data = data.filter((r) => r.type === filters.type)
      }
      // View filter is handled server-side; mock just returns all
      return {
        data,
        meta: { total: data.length, page: 1, per_page: 20, total_pages: 1 },
      }
    }
    return recognitionApi.getRecognitions(filters)
  }

  async createRecognition(
    payload: CreateRecognitionPayload,
  ): Promise<Recognition[]> {
    if (USE_MOCK) {
      const sender = mockEmployees[0]
      return payload.recipient_ids.map((recipientId) => {
        const recipient =
          mockEmployees.find((e) => e.id === recipientId) ||
          mockEmployees[1]
        return {
          id: `mock-${Date.now()}-${recipientId}`,
          sender,
          recipients: [recipient],
          type: payload.type,
          message: payload.message,
          value: payload.value,
          badge: null,
          reactions: { clap: 0, heart: 0, celebrate: 0, fire: 0 },
          comments: [],
          time: 'Just now',
          points: payload.points,
        }
      })
    }
    return recognitionApi.createRecognition(payload)
  }

  async addReaction(payload: AddReactionPayload): Promise<{ added: boolean }> {
    if (USE_MOCK) return { added: true }
    return recognitionApi.addReaction(payload)
  }

  async addComment(payload: AddCommentPayload): Promise<Comment> {
    if (USE_MOCK) {
      return {
        id: `mock-comment-${Date.now()}`,
        user: mockEmployees[0],
        text: payload.text,
        time: 'Just now',
      }
    }
    return recognitionApi.addComment(payload)
  }

  async getLeaderboard(
    filters?: LeaderboardFilters,
  ): Promise<{ data: LeaderboardEntry[]; total: number }> {
    if (USE_MOCK) return { data: mockLeaderboard, total: mockLeaderboard.length }
    return recognitionApi.getLeaderboard(filters)
  }

  async getBadges(category?: string): Promise<BadgeItem[]> {
    if (USE_MOCK) {
      if (category && category !== 'all') {
        return mockBadges.filter((b) => b.category === category)
      }
      return mockBadges
    }
    return recognitionApi.getBadges(category)
  }

  async getRewards(category?: string): Promise<Reward[]> {
    if (USE_MOCK) {
      if (category && category !== 'all') {
        return mockRewards.filter((r) => r.category === category)
      }
      return mockRewards
    }
    return recognitionApi.getRewards(category)
  }

  async redeemReward(payload: RedeemRewardPayload): Promise<void> {
    if (USE_MOCK) return
    return recognitionApi.redeemReward(payload)
  }

  async getMyStats(): Promise<RecognitionStats> {
    if (USE_MOCK) return mockStats
    // Use the dashboard endpoint to get real stats
    const dashboard = await this.getDashboard()
    return dashboard.my_stats
  }

  async getDashboard(): Promise<DashboardData> {
    if (USE_MOCK) {
      return {
        my_stats: mockStats,
        trending_values: [],
        top_recognized: [],
      }
    }
    return recognitionApi.getDashboard()
  }

  async getAnalytics(): Promise<RecognitionAnalytics> {
    if (USE_MOCK) {
      return {
        chartData: analyticsChartData,
        months: analyticsMonths,
        deptAnalytics,
        valueDistribution,
        kpis: {
          totalRecognitions: 1247,
          participationRate: 78,
          avgPerEmployee: 4.2,
          budgetSpent: '2.4L',
          topValue: 'Innovation',
        },
      }
    }
    return recognitionApi.getAnalytics()
  }

  async searchEmployees(query: string): Promise<Employee[]> {
    if (USE_MOCK) {
      return mockEmployees.filter((e) =>
        e.name.toLowerCase().includes(query.toLowerCase()),
      )
    }
    // Use the RBAC employees endpoint with search support
    return recognitionApi.fetchEmployeesForRecognition('kudos')
  }

  /**
   * Fetch employees based on recognition type:
   * - spot_award: RBAC-scoped (manager sees reportees, HR sees scoped employees)
   * - all others: Returns all employees (no RBAC)
   */
  async getEmployeesForRecognitionType(type: RecognitionType): Promise<Employee[]> {
    if (USE_MOCK) {
      if (type === 'spot_award') {
        // Mock: simulate manager scope — return only first 5 employees (manager's "reportees")
        return mockEmployees.slice(0, 5)
      }
      // All other types: return everyone
      return mockEmployees
    }
    return recognitionApi.fetchEmployeesForRecognition(type)
  }

  async getCompanyValues() {
    if (USE_MOCK) return mockCompanyValues.map((v) => ({ id: v, name: v, color: null }))
    return recognitionApi.getCompanyValues()
  }

  async createCompanyValue(payload: CreateCompanyValuePayload) {
    return recognitionApi.createCompanyValue(payload)
  }

  async deleteCompanyValue(valueId: string) {
    return recognitionApi.deleteCompanyValue(valueId)
  }
}

export const recognitionService = new RecognitionService()

